import { useQuery } from '@tanstack/react-query';
import type { PlanFeatures, UsageSummary } from '@storeflare/types';
import { Page } from '@/components/Page';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';

/**
 * Shows the org's current plan, its feature flags, and current usage
 * relative to plan limits. Wired against:
 *   GET /v1/plan         — current plan + features (returns the org's plan row)
 *   GET /v1/usage        — aggregate counters in the current 30-day window
 *
 * No upgrade button — billing flows ship via the billing partner; this
 * page is read-only and points to the docs for plan changes.
 */
interface PlanInfo {
  plan: { id: string; name: string; features: PlanFeatures };
  org: { id: string; name: string };
}

export function PlanPage() {
  const plan = useQuery({
    queryKey: ['plan'],
    queryFn: () => api<PlanInfo>('/v1/plan'),
  });
  const usage = useQuery({
    queryKey: ['usage'],
    queryFn: () => api<UsageSummary>('/v1/usage'),
  });

  if (plan.isLoading || usage.isLoading) {
    return (
      <Page title="Plan">
        <Skeleton className="h-40 w-full" />
      </Page>
    );
  }

  const features = plan.data?.plan.features;

  return (
    <Page title="Plan" subtitle="Your current subscription, limits and usage.">
      <div className="grid grid-cols-12 gap-4">
        <Card className="col-span-12 p-4 md:col-span-6">
          <div className="text-xs uppercase text-muted-foreground">Current plan</div>
          <div className="mt-1 flex items-center gap-2">
            <h2 className="text-lg font-semibold tracking-tight">{plan.data?.plan.name ?? '—'}</h2>
            <Badge variant="secondary">{plan.data?.org.name}</Badge>
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            Plan changes are managed through the billing portal — contact support to upgrade or downgrade.
          </p>
        </Card>

        <Card className="col-span-12 p-4 md:col-span-6">
          <div className="text-xs uppercase text-muted-foreground">Features</div>
          <ul className="mt-2 grid grid-cols-2 gap-2 text-sm">
            <FeatureRow label="Image transform" on={features?.transform_enabled} />
            <FeatureRow label="Rate limit / min" value={features?.rate_limit_rpm} />
            <FeatureRow label="Webhook events" value={(features?.webhook_events ?? []).join(', ') || '—'} />
            <FeatureRow label="Audit retention" value={features ? `${features.audit_log_retention_days}d` : '—'} />
          </ul>
        </Card>

        {features ? (
          <>
            <UsageBar
              className="col-span-12 md:col-span-6"
              label="Storage"
              used={usage.data?.storage_bytes ?? 0}
              limit={features.max_storage_gb * 1024 ** 3}
              format={fmtBytes}
            />
            <UsageBar
              className="col-span-12 md:col-span-6"
              label="Transform calls this month"
              used={usage.data?.transform_calls ?? 0}
              limit={features.transform_calls_per_month}
              format={(n) => n.toLocaleString()}
            />
            <UsageBar
              className="col-span-12 md:col-span-6"
              label="API tokens"
              used={null}
              limit={features.max_api_tokens}
              format={(n) => n.toLocaleString()}
            />
            <UsageBar
              className="col-span-12 md:col-span-6"
              label="Buckets"
              used={null}
              limit={features.max_buckets}
              format={(n) => n.toLocaleString()}
            />
          </>
        ) : null}
      </div>
    </Page>
  );
}

function FeatureRow({
  label,
  on,
  value,
}: {
  label: string;
  on?: boolean;
  value?: string | number | null;
}) {
  return (
    <li className="flex items-center justify-between rounded-md border px-3 py-1.5">
      <span className="text-muted-foreground">{label}</span>
      {typeof on === 'boolean' ? (
        <Badge variant={on ? 'secondary' : 'outline'}>{on ? 'on' : 'off'}</Badge>
      ) : (
        <span className="font-medium tabular-nums">{value ?? '—'}</span>
      )}
    </li>
  );
}

function UsageBar({
  label,
  used,
  limit,
  format,
  className,
}: {
  label: string;
  used: number | null;
  limit: number;
  format: (n: number) => string;
  className?: string;
}) {
  const pct = used == null || limit <= 0 ? 0 : Math.min(100, Math.round((used / limit) * 100));
  return (
    <Card className={`p-4 ${className ?? ''}`}>
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">{label}</span>
        <span className="tabular-nums">
          {used == null ? '—' : format(used)} <span className="text-muted-foreground">/ {format(limit)}</span>
        </span>
      </div>
      <div className="mt-2 h-2 w-full overflow-hidden rounded bg-muted">
        <div
          className={`h-full ${pct >= 90 ? 'bg-destructive' : 'bg-primary'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </Card>
  );
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  return `${(n / 1024 ** 3).toFixed(2)} GB`;
}
